import {
    Brands,
    Championships,
    db,
    Factions,
    GimmickMatches,
    Superstars,
    TagTeams,
    TalentRequests
} from "../models/models.js";
import {
    addCompanyCompanyAlerts,
    addCompanyMessages,
    addCompanyTalentRequests,
    getCompanyChampionships,
    getCompanyEvents,
    getCompanyGame,
    getCompanyGimmickMatches,
    getCompanyStaff,
    getCompanyTalentRequests
} from "../models/company.js";
import {getTalentRequestSuperstar, getTalentRequestTagTeam} from "../models/talentRequest.js";
import {talentRequestCommonDialogs, talentRequestsData} from "../data/talentRequests.js";
import {
    formatStringWithParams,
    getArrayMaxByProperty,
    getCurrentGame,
    getMonthsDifference,
    getRandomChoice,
    getRandomInt,
    getRosterMemberAge,
    getRosterMemberPopularity,
    objectInArray,
    toTitleCase,
    weightedChoice
} from "./helpers.js";
import {
    createTalentRequestDeclinedMessage,
    createTalentRequestFailedMessage,
    createTalentRequestSucceededMessage
} from "./messages.js";
import {
    checkFacilityUpgrade,
    createCompanyAlert,
    getAvailableStaffMembersByRole,
    getCompanyRosterByType,
    getCompanyUnreadMessages,
    getRandomStaffMemberByRole,
    getStaffAgeMultiplier,
    updateStaffMemberXp
} from "./companies.js";
import {
    checkAvailability,
    checkSuperstarBrandAvailability,
    getEmptySuperstarGraphic,
    getNextRank,
    getRankFromPopularity,
    getRosterMemberSuperstars,
    getSuperstarActiveFactions,
    updateSuperstarMorale
} from "./superstars.js";
import {getRosterActiveFactions} from "./factions.js";
import {getEventMatchesAndSegments, getSuperstarsUsedAtEvent} from "./events.js";
import {getSuperstarTagTeams} from "../models/superstar.js";
import {getRosterBrands, getRosterSuperstars, getRosterTagTeams} from "../models/roster.js";
import {getFactionSuperstars} from "../models/faction.js";
import {getChampionshipCurrentReign} from "../models/championship.js";
import {getRosterMemberFeuds} from "./feuds.js";
import {getFeudParticipants} from "../models/feud.js";
import {getGameCurrentUserCompany} from "./games.js";
import {loadUser, saveUser} from "./user.js";

async function getCompanyTalentRequestsData(company) {  // Previously get_company_talent_requests_data
    const companyTalentRequests = await getCompanyTalentRequests(company);
    let activeTalentRequests = companyTalentRequests.filter(x => ['created', 'active'].indexOf(x.status) >= 0);
    activeTalentRequests.sort((a, b) => a.$loki - b.$loki);
    return Promise.all(
        activeTalentRequests.map(x => {
            return getTalentRequestData(x)
        })
    );
}

async function getTalentRequestData(talentRequest) {  // get_talent_request_data
    let data = {};
    const talent = await getTalentRequestTalent(talentRequest);
    const changeAllowed = await checkTalentRequestChangeAllowed(talentRequest, talent);

    data['talentName'] = talent.name;
    data['name'] = await getTalentRequestName(talentRequest);
    data['monthsRemaining'] = talentRequest.monthsRemaining;
    data['uid'] = talentRequest.$loki;
    data['talentUid'] = talent.$loki;
    data['talentType'] = talent.objectName;
    data['type'] = talentRequest.type;
    data['importance'] = talentRequestsData[talentRequest.type]['importance'];
    data['dialog'] = await getTalentRequestDialog(talentRequest, talent);
    data['status'] = talentRequest.status;
    data['changeAllowed'] = changeAllowed;

    // Get graphic
    const topSuperstar = getArrayMaxByProperty(await getRosterMemberSuperstars(talent), 'popularity');
    let topSuperstarGraphic = topSuperstar.graphic;
    if (!topSuperstarGraphic || Object.keys(topSuperstarGraphic).length === 0) {
        topSuperstarGraphic = getEmptySuperstarGraphic(topSuperstar);
    }
    data['graphic'] = [topSuperstarGraphic];

    // Get responses
    data['responses'] = getTalentRequestResponses(talentRequest);

    // Get change dialog
    data['changeDialog'] = getTalentRequestChangeDialog(talentRequest, changeAllowed);

    return data;
}

async function checkTalentRequestChangeAllowed(talentRequest, talent) {  // check_talent_request_change_allowed
    const rosterMemberSuperstars = await getRosterMemberSuperstars(talent);
    const hasEasyToPleaseOrLoyalTrait = rosterMemberSuperstars.some(superstar =>
        ['Easy To Please', 'Loyal'].some(trait => superstar.traits.includes(trait))
    );
    const hasHighMorale = rosterMemberSuperstars.some(superstar => superstar.morale >= 60);
    return (hasEasyToPleaseOrLoyalTrait || hasHighMorale) && !talentRequest.changedRequest;

}

function getTalentRequestResponses(talentRequest) {  // get_talent_request_responses
    const data = {};
    const talentRequestRandom = new Math.seedrandom(talentRequest.meta.created);
    data['acceptRequest'] = getRandomChoice(talentRequestCommonDialogs['acceptRequest'], talentRequestRandom());
    data['declineRequest'] = getRandomChoice(talentRequestCommonDialogs['declineRequest'], talentRequestRandom());
    data['changeRequest'] = getRandomChoice(talentRequestCommonDialogs['changeRequest'], talentRequestRandom());
    return data;
}

function getTalentRequestChangeDialog(talentRequest, changeAllowed) {  // get_talent_request_change_dialog
    const talentRequestRandom = new Math.seedrandom(talentRequest.meta.created);
    const changeDialogs = changeAllowed ? talentRequestCommonDialogs['changeAccepted'] : talentRequestCommonDialogs['changeDeclined'];
    return getRandomChoice(changeDialogs, talentRequestRandom());
}

async function getTalentRequestDialog(talentRequest, talent = null, declined = false) {  // get_talent_request_dialog
    const talentRequestRandom = new Math.seedrandom(talentRequest.meta.created);
    let talentRequestData = talentRequestsData[talentRequest.type];
    let dialog;

    // Get talent request's creation dialog
    if (['created', 'active'].includes(talentRequest.status)) {
        let talentName = null;
        let talentPronoun = null;
        let talentPossessive = null;
        let talentObjective = null;
        let teamName = null;
        let factionName = null;
        let championshipName = null;
        let brandName = null;
        let currentBrandName = null;
        let gimmickMatchName = null;
        let alignment = null;
        let currentAlignment = null;
        let skill = null;

        if (talentRequest.actionData['selectedTalentId']) {
            let selectedTalent = await Superstars.get(talentRequest.actionData['selectedTalentId']);
            if (!selectedTalent) {
                selectedTalent = await TagTeams.get(talentRequest.actionData['selectedTalentId']);
            }
            talentName = selectedTalent.name;
            talentPronoun = selectedTalent.pronoun;
            talentPossessive = selectedTalent.possessivePronoun;
            talentObjective = selectedTalent.objectivePronoun;
        }
        if (talentRequest.actionData['selectedTeamId']) {
            let team = await TagTeams.get(talentRequest.actionData['selectedTeamId']);
            if (team) {
                teamName = team.name;
            }
        }
        if (talentRequest.actionData['selectedFactionId']) {
            let faction = await Factions.get(talentRequest.actionData['selectedFactionId']);
            if (faction) {
                factionName = faction.name;
            }
        }
        if (talentRequest.actionData['selectedChampionshipId']) {
            let championship = await Championships.get(talentRequest.actionData['selectedChampionshipId']);
            if (championship) {
                championshipName = championship.name;
            }
        }
        if (talentRequest.actionData['selectedBrandId']) {
            let brand = await Brands.get(talentRequest.actionData['selectedBrandId']);
            if (brand) {
                brandName = brand.name;
            }
            currentBrandName = (await getRosterMemberSuperstars(talent))[0].brandName;
        }
        if (talentRequest.actionData['selectedGimmickMatchId']) {
            let gimmickMatch = await GimmickMatches.get(talentRequest.actionData['selectedGimmickMatchId']);
            if (gimmickMatch) {
                gimmickMatchName = gimmickMatch.name;
            }
        }
        if (talentRequest.actionData['selectedAlignment']) {
            alignment = talentRequest.actionData['selectedAlignment'];
            currentAlignment = talent.alignment;
        }
        if (talentRequest.actionData['selectedSkill']) {
            skill = talentRequest.actionData['selectedSkill'];
        }

        let rank = talentRequest.actionData['selectedRank'];
        let rankIndefiniteArticle = rank === 'Upper Midcarder' ? 'an' : 'a';

        let dialogChoice = getRandomChoice(talentRequestData.dialogs, talentRequestRandom());
        dialog = formatStringWithParams(dialogChoice,
            {
                rank, rankIndefiniteArticle, talentName, teamName, factionName, championshipName,
                talentPronoun, talentPossessive, talentObjective, brandName, gimmickMatchName, alignment, currentAlignment,
                currentBrandName, skill
            });
    }
    // Get from common dialogs
    else if (['completed', 'failed'].includes(talentRequest.status)) {
        let dialogs;
        if (declined) {
            dialogs = talentRequestCommonDialogs['requestDeclined'][talentRequestData.importance];
        } else if (talentRequest.status === 'completed') {
            dialogs = talentRequestCommonDialogs['requestCompleted'];
        } else if (talentRequest.status === 'failed') {
            dialogs = talentRequestCommonDialogs['requestFailed'];
        }
        dialog = getRandomChoice(dialogs, talentRequestRandom());
    }

    return dialog;
}

async function acceptTalentRequestView({uid} = {}) {  // accept_talent_request_view
    let data = {}

    // Get talent request
    let talentRequest = await TalentRequests.get(uid);
    if (!talentRequest) {
        data['error'] = 'talent-request-not-found';
        return data;
    }

    // Accept talent request
    talentRequest.status = 'active';

    db.saveDatabase();

    // Get dialog
    data['dialog'] = await getTalentRequestAcceptedDialog(talentRequest);

    return data;
}

function getTalentRequestAcceptedDialog(talentRequest) {  // get_talent_request_accepted_dialog
    const talentRequestRandom = new Math.seedrandom(talentRequest.meta.created);
    return getRandomChoice(talentRequestCommonDialogs['requestAccepted'], talentRequestRandom())
}

async function declineTalentRequestView({uid} = {}) {  // decline_talent_request_view
    let data = {}
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);

    // Get talent request
    let talentRequest = await TalentRequests.get(uid);
    if (!talentRequest) {
        data['error'] = 'talent-request-not-found';
        return data;
    }

    // Decline talent request
    await declineTalentRequest(talentRequest, userCompany, currentGame);

    // Get dialog
    data['dialog'] = await getTalentRequestDialog(talentRequest, null, true);

    // Delete request
    await TalentRequests.remove(talentRequest);

    db.saveDatabase();

    // Get unread messages
    let unreadMessages = await getCompanyUnreadMessages(userCompany);
    data['unreadMessages'] = unreadMessages.length;

    return data;
}

async function declineTalentRequest(talentRequest, company, game) {
    talentRequest.status = 'failed';
    let talent = await getTalentRequestTalent(talentRequest);

    // Update morale
    let moraleChange = await getTalentRequestMoraleChange(talentRequest, true);
    for (let superstar of (await getRosterMemberSuperstars(talent))) {
        updateSuperstarMorale(superstar, moraleChange)
    }

    // Add message
    let talentManager = getRandomStaffMemberByRole((await getCompanyStaff(company)), 'talent_manager');
    let newMessage = await createTalentRequestDeclinedMessage(talentRequest, talent, moraleChange, game.monthCount,
        game.year, talentManager);
    await addCompanyMessages(company, [newMessage]);

    return true;
}

async function changeTalentRequestView({uid} = {}) {  // change_talent_request_view
    let data = {}
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);
    let mainRoster = await getCompanyRosterByType(userCompany, 'main');
    let rosterSuperstars = await getRosterSuperstars(mainRoster);
    let championships = await getCompanyChampionships(userCompany);

    // Get talent request
    let talentRequest = await TalentRequests.get(uid);
    if (!talentRequest) {
        data['error'] = 'talent-request-not-found';
        return data;
    }

    // Change talent request
    let newTalentRequest = await changeTalentRequest(talentRequest, userCompany, rosterSuperstars, mainRoster,
        championships)

    db.saveDatabase();

    // Get data for new talent request
    data['newTalentRequestData'] = await getTalentRequestData(newTalentRequest);

    return data;
}

async function changeTalentRequest(talentRequest, company, rosterSuperstars, roster, championships) {  // change_talent_request
    let talent = await getTalentRequestTalent(talentRequest);

    // Get new talent request data
    let requestObjects = await getObjectsForChangeTalentRequest(talentRequest, talent, company, rosterSuperstars, roster,
        championships);
    let [requestType, requestMonths, actionData] = requestObjects;

    // Create changed talent request, add to company
    let newTalentRequest = await createTalentRequest(talent, requestType, requestMonths, actionData);
    newTalentRequest.changedRequest = true;
    await addCompanyTalentRequests(company, [newTalentRequest]);

    // Delete old request
    await TalentRequests.remove(talentRequest);

    return newTalentRequest;
}

async function getObjectsForChangeTalentRequest(talentRequest, talent, company, rosterSuperstars, roster, championships) {  // get_objects_for_change_talent_request
    let actionData = {};
    let tagTeams = await getRosterTagTeams(roster);
    let rosterFactions = await getRosterActiveFactions(roster);

    // Get available superstars
    let superstars = rosterSuperstars.filter(x => checkAvailability(x, true, false, false) &&
        x.activeEffect !== 'traded' && !x.fullTimeManager);

    // Get request type
    let requestType = await getRequestTypeForChangeTalentRequest(talentRequest, talent, company, superstars, tagTeams,
        rosterFactions, championships, roster, rosterSuperstars);

    // Get action data, if applicable
    if (talentRequestsData[requestType]['get_action_data']) {
        actionData = await getActionDataForTalentRequest(requestType, talent, superstars, tagTeams, championships,
            roster, company);

        // If action data error, get from basic request types
        if (!actionData || Object.keys(actionData).length === 0) {
            if (talent.fullTimeManager) {
                requestType = 'solo_promo_segment'
            } else {
                requestType = getRandomChoice(['be_in_main_event', 'solo_promo_segment'])
            }
        }
    }

    // Get request months
    let requestMonths = getRandomChoice(talentRequestsData[requestType]['months']);

    return [requestType, requestMonths, actionData];
}

async function getRequestTypeForChangeTalentRequest(talentRequest, talent, company, superstars, teams, rosterFactions,
                                                    championships, roster, rosterSuperstars) {  // get_request_type_for_change_talent_request
    // Go through all request types, add those that talent is eligible for
    let requestTypesWeights = [];
    let rarityWeights = {'common': 3.8, 'normal': 3.5, 'uncommon': 2.15, 'rare': 1.25};

    for (let [requestType, v] of Object.entries(talentRequestsData)) {
        if (requestType !== talentRequest.type &&
            !talentRequestsData[talentRequest.type]['conflicting_requests'].includes(requestType) &&
            (!talent.fullTimeManager || talentRequestsData[requestType]['allow_managers'])) {
            let addRequestType = false;

            if (['be_in_main_event', 'feud_with_x', 'match_with_x', 'solo_promo_segment', 'change_appearance']
                .includes(requestType)) {
                addRequestType = true
            }

            if (requestType === 'go_to_next_rank') {
                let nextRank = await getNextRank(talent);
                if (nextRank) {
                    let nextRankPopularity = {'Main Eventer': 800, 'Upper Midcarder': 600, 'Lower Midcarder': 400};
                    let popularityToNextRank = nextRankPopularity[nextRank] - talent.popularity;
                    if (10 < popularityToNextRank && popularityToNextRank < 80) {
                        addRequestType = true;
                    }
                }
            }

            if (['no_clean_loss', 'beat_x_clean', 'win_and_not_lose'].includes(requestType)) {
                if (getRankFromPopularity(talent.popularity) === 'Main Eventer' ||
                    (await getRosterMemberSuperstars(talent)).some(y => y.unhappy) ||
                    ['Rebel', 'Hard To Please'].some(y => talent.traits.includes(y))) {
                    addRequestType = true;
                }
            }

            if (requestType === 'put_x_over_clean') {
                if (['Spotlight Lover', 'Giving Veteran', 'Star Maker'].some(y => talent.traits.includes(y))) {
                    addRequestType = true;
                }
            }

            if (['be_in_title_match', 'win_a_championship'].includes(requestType)) {
                if (!championships.some(c => c.superstarId === talent.$loki)) {
                    addRequestType = true;
                }
            }

            if (requestType === 'lose_championship') {
                if (championships.some(c => c.superstarId === talent.$loki && !c.matchContract && !c.championshipContract)) {
                    if (['Big Stage Wreck', 'Other Interests', 'Extreme Emotions'].some(y => talent.traits.includes(y)) ||
                        (await getRosterMemberSuperstars(talent)).some(y => y.contractMonthsRemaining)) {
                        addRequestType = true;
                    }
                }
            }

            if (requestType === 'be_in_gimmick_match') {
                if (['Risk Taker', 'Gifted Acrobat', 'Enjoys Gimmick Matches'].some(y => talent.traits.includes(y))) {
                    addRequestType = true;
                }
            }

            if (requestType === 'team_with_x') {
                let superstarTagTeam = (await getSuperstarTagTeams(talent)).find(y => !y.temporary);
                if (!superstarTagTeam) {
                    addRequestType = true;
                }
            }
            if (requestType === 'disband_team') {
                let superstarTagTeam = (await getSuperstarTagTeams(talent)).find(y => !y.temporary);
                if (talent.hot && superstarTagTeam) {
                    addRequestType = true;
                }
            }

            if (requestType === 'write_off') {
                if (talent.partTimer || talent.traits.includes('Other Interests')) {
                    addRequestType = true;
                }
            }

            if (requestType === 'lose_next_match') {
                if (talent.winsCounter >= 2 && ['Giving Veteran', 'Star Maker'].some(y => talent.traits.includes(y))) {
                    addRequestType = true;
                }
            }

            if (requestType === 'beat_main_eventer') {
                if (getRankFromPopularity(talent.popularity) === 'Upper Midcarder') {
                    addRequestType = true;
                }
            }

            if (requestType === 'win_next_match') {
                if (talent.lossesCounter >= 2) {
                    addRequestType = true;
                }
            }

            if (requestType === 'be_managed') {
                if (!talent.managerId && !talent.fullTimeManager && talent.charisma <= 2) {
                    addRequestType = true;
                }
            }

            if (requestType === 'manage_x') {
                let managerIds = [...new Set(superstars.filter(x => x.managerId).map(x => x.managerId))];
                if (talent.fullTimeManager && talent.charisma >= 2 && !managerIds.includes(talent.$loki)) {
                    addRequestType = true;
                }
            }

            if (requestType === 'start_faction') {
                let rosterFactionSuperstars = (await Promise.all(rosterFactions.map(x => getFactionSuperstars(x)))).flat();
                if (!objectInArray(talent, rosterFactionSuperstars)) {
                    addRequestType = true;
                }
            }

            if (requestType === 'leave_faction') {
                let rosterFactionSuperstars = (await Promise.all(rosterFactions.map(x => getFactionSuperstars(x)))).flat();
                if (talent.hot && objectInArray(talent, rosterFactionSuperstars)) {
                    addRequestType = true;
                }
            }

            if (requestType === 'no_weekly_shows') {
                let game = await getCompanyGame(company);
                if (game.numberOfWeeklyShows !== 0 && (talent.partTimer ||
                    ['Other Interests', 'Weekly Sloth'].some(y => talent.traits.includes(y)))) {
                    addRequestType = true;
                }
            }
            if (requestType === 'customize_championship') {
                let game = await getCompanyGame(company);
                let superstarChampionships = championships.filter(c => c.superstarId === talent.$loki && !c.matchContract &&
                    !c.championshipContract);
                for (let c of superstarChampionships) {
                    let reign = await getChampionshipCurrentReign(c);
                    if (reign.endMonth === null &&
                        getMonthsDifference(game.monthCount, game.year, reign.startMonth, reign.startYear) < 4) {
                        addRequestType = true;
                        break
                    }
                }
            }

            if (requestType === 'change_alignment') {
                if (talent.alignment) {
                    addRequestType = true;
                }
            }

            if (requestType === 'debut_new_gimmick') {
                if (talent.overness < 40) {
                    addRequestType = true;
                }
            }

            if (requestType === 'vacate_championship') {
                if (championships.some(c => c.superstarId === talent.$loki && !c.matchContract && !c.championshipContract
                    && !c.tagTeam && !c.trio) && (['Rebel', 'Hard To Please'].some(y => talent.traits.includes(y)) ||
                    (await getRosterMemberSuperstars(talent)).some(y => y.unhappy))) {
                    addRequestType = true;
                }
            }

            if (requestType === 'be_in_gimmick_match_x') {
                if (['Risk Taker', 'Gifted Acrobat', 'Enjoys Gimmick Matches'].some(y => talent.traits.includes(y)) ||
                    talent.traits.some(y => y.split(' ')[y.split(' ').length - 1] === 'Expert')) {
                    addRequestType = true;
                }
            }

            if (requestType === 'join_faction') {
                let rosterFactionSuperstars = (await Promise.all(rosterFactions.map(x => getFactionSuperstars(x)))).flat();
                if (rosterFactions.length > 0 && !objectInArray(talent, rosterFactionSuperstars)) {
                    addRequestType = true;
                }
            }

            if (requestType === 'change_brand') {
                let exclusiveRosterBrands = (await getRosterBrands(roster)).filter(y => y.weeklyShowIsExclusive &&
                    y.name !== talent.brandName);
                if (exclusiveRosterBrands.length > 0 && talent.brandName) {
                    addRequestType = true;
                }
            }

            if (requestType === 'be_on_monthly_event') {
                // Check if talent not featured on last monthly event
                let latestHeldEvent = (await getCompanyEvents(company)).filter(x => x.rating !== null).sort((a, b) => b.$loki - a.$loki)[0];
                let matchesAndSegments = await getEventMatchesAndSegments(latestHeldEvent);
                let superstarsUsedAtEvent = await getSuperstarsUsedAtEvent(matchesAndSegments, rosterSuperstars);
                if (!objectInArray(talent, superstarsUsedAtEvent) && checkSuperstarBrandAvailability(latestHeldEvent, talent)) {
                    addRequestType = true;
                }
            }

            if (requestType === 'train_skill_x') {
                if (getAvailableStaffMembersByRole((await getCompanyStaff(company)), 'trainer').some(x => !x.currentTask)) {
                    if (talent.workrate < 3 || talent.charisma < 3) {
                        addRequestType = true;
                    }
                }
            }

            // Add request type with weights
            if (addRequestType) {
                let weight = rarityWeights[v.rarity];
                requestTypesWeights.push([requestType, weight]);
            }
        }
    }

    // Select request type
    return weightedChoice(requestTypesWeights);
}

async function getTalentRequestTalent(talentRequest) {  // Previously get_talent_request_talent
    if (talentRequest.superstarId) {
        return getTalentRequestSuperstar(talentRequest)
    }
    else {
        return getTalentRequestTagTeam(talentRequest)
    }
}

async function getTalentRequestName(talentRequest) {  // Previously get_talent_request_name
    let talentName = null;
    let teamName = null;
    let factionName = null;
    let championshipName = null;
    let brandName = null;
    let gimmickMatchName = null;
    let alignment = null;
    let skill = null;
    let rank = null;

    // Get action data objects
    if (talentRequest.actionData['selectedTalentId']) {
        let talent = await Superstars.get(talentRequest.actionData['selectedTalentId']);
        if (!talent) {
            talent = await TagTeams.get(talentRequest.actionData['selectedTalentId'])
        }
        talentName = talent.name;
    }
    if (talentRequest.actionData['selectedTeamId']) {
        let team = await TagTeams.get(talentRequest.actionData['selectedTeamId']);
        teamName = team.name;
    }
    if (talentRequest.actionData['selectedFactionId']) {
        let faction = await Factions.get(talentRequest.actionData['selectedFactionId']);
        factionName = faction.name;
    }
    if (talentRequest.actionData['selectedChampionshipId']) {
        let championship = await Championships.get(talentRequest.actionData['selectedChampionshipId']);
        championshipName = championship?.name;
    }
    if (talentRequest.actionData['selectedBrandId']) {
        let brand = await Brands.get(talentRequest.actionData['selectedBrandId']);
        brandName = brand.name;
    }
    if (talentRequest.actionData['selectedGimmickMatchId']) {
        let gimmickMatch = await GimmickMatches.get(talentRequest.actionData['selectedGimmickMatchId']);
        gimmickMatchName = gimmickMatch.name;
    }
    if (talentRequest.actionData['selectedAlignment']) {
        alignment = toTitleCase(talentRequest.actionData['selectedAlignment'])
    }
    if (talentRequest.actionData['selectedSkill']) {
        skill = toTitleCase(talentRequest.actionData['selectedSkill'])
    }
    if (talentRequest.actionData['selectedRank']) {
        rank = toTitleCase(talentRequest.actionData['selectedRank'])
    }

    // Format name using objects
    const talentRequestNameData = talentRequestsData[talentRequest.type]['name'];
    return formatStringWithParams(talentRequestNameData,
        {
            talentName, teamName, factionName, championshipName, brandName, gimmickMatchName, alignment, skill, rank
        });
}

async function checkTalentRequestAction({company, requestType, talent, data = null, secondaryAction = false,
                                         failRequest = false} = {}) {  // check_talent_request_action
    const game = await getCompanyGame(company);

    // Get talent request from company's talent requests
    const companyTalentRequests = (await getCompanyTalentRequests(company))
        .filter(x => ['created', 'active'].includes(x.status) && x.type === requestType);
    let talentRequest;
    for (const x of companyTalentRequests) {
        let t = await getTalentRequestTalent(x);
        if (t.objectName === talent.objectName && t.$loki === talent.$loki) {
            talentRequest = x;
            break;
        }
    }

    // Check result of talent request
    if (talentRequest) {
        let completed = false;
        let failed = false;

        if (['be_in_main_event', 'win_a_championship', 'be_in_gimmick_match', 'write_off', 'change_appearance',
            'beat_main_eventer', 'solo_promo_segment', 'be_in_title_match', 'be_managed', 'start_faction', 'debut_new_gimmick',
            'be_on_monthly_event'].includes(requestType)) {
            completed = true;
        }
        if (['put_x_over_clean', 'feud_with_x', 'team_with_x', 'beat_x_clean', 'match_with_x', 'manage_x'].includes(requestType)) {
            if (data['talentId'] === talentRequest.actionData['selectedTalentId']) {
                completed = true;
            }
        }
        if (['lose_championship', 'customize_championship'].includes(requestType)) {
            if (data['championshipId'] === talentRequest.actionData['selectedChampionshipId']) {
                completed = true;
            }
        }
        if (requestType === 'go_to_next_rank') {
            if (data['reachedRank'] === talentRequest.actionData['selectedRank']) {
                completed = true;
            }
        }
        if (requestType === 'disband_team') {
            if (data['teamId'] === talentRequest.actionData['selectedTeamId']) {
                completed = true;
            }
        }
        if (['leave_faction', 'join_faction'].includes(requestType)) {
            if (data['factionId'] === talentRequest.actionData['selectedFactionId']) {
                completed = true;
            }
        }
        if (['no_clean_loss', 'no_weekly_shows'].includes(requestType)) {
            if (failRequest) {
                failed = true;
            }
        }
        if (requestType === 'win_and_not_lose') {
            if (secondaryAction) {
                talentRequest.actionCompleted = true;
            } else if (failRequest) {
                failed = true;
            }
        }
        if (requestType === 'change_alignment') {
            if (data['alignment'] === talentRequest.actionData['selectedAlignment']) {
                completed = true;
            }
        }
        if (requestType === 'be_in_gimmick_match_x') {
            if (data['gimmickMatchId'] === talentRequest.actionData['selectedGimmickMatchId']) {
                completed = true;
            }
        }
        if (requestType === 'change_brand') {
            if (data['brandId'] === talentRequest.actionData['selectedBrandId']) {
                completed = true;
            }
        }
        if (requestType === 'vacate_championship') {
            if (data['championshipId'] === talentRequest.actionData['selectedChampionshipId']) {
                if (failRequest) {
                    // Lost championship
                    failed = true;
                } else {
                    completed = true;
                }
            }
        }
        if (requestType === 'train_skill_x') {
            if (data['skill'] === talentRequest.actionData['selectedSkill']) {
                completed = true;
            }
        }
        if (['win_next_match', 'lose_next_match'].includes(requestType)) {
            if (failRequest) {
                failed = true;
            } else {
                completed = true;
            }
        }

        // Complete/fail request
        if (completed) {
            await completeTalentRequest(talentRequest, company, game);
        } else if (failed) {
            await failTalentRequest(talentRequest, company, game);
        }
    }

    return talentRequest;
}


async function completeTalentRequest(talentRequest, company, game) {  // complete_talent_request
    talentRequest.status = 'completed';
    const talent = await getTalentRequestTalent(talentRequest);
    const talentManager = getRandomStaffMemberByRole((await getCompanyStaff(company)), 'talent_manager');

    // Add company alert
    const talentRequestText = `${talent.name} (${await getTalentRequestName(talentRequest)})`;
    const newCompanyAlert = await createCompanyAlert('talentRequestCompleted', talentRequestText);
    await addCompanyCompanyAlerts(company, [newCompanyAlert]);

    // Update morale
    const moraleChange = await getTalentRequestMoraleChange(talentRequest);
    for (const superstar of (await getRosterMemberSuperstars(talent))) {
        updateSuperstarMorale(superstar, moraleChange);
    }

    // Add actions completed (Superstars Reassured)
    await checkFacilityUpgrade('Talent Management', company);

    // Add talent manager xp
    let xpGained = 0;
    if (talentManager) {
        xpGained = 8;
        const ageMultiplier = getStaffAgeMultiplier(talentManager);
        xpGained = Math.round(xpGained * ageMultiplier);
        await updateStaffMemberXp(talentManager, xpGained, company, game);
    }

    // Add message
    const newMessage = await createTalentRequestSucceededMessage(talentRequest, talent, moraleChange, game.monthCount,
        game.year, talentManager, xpGained);
    await addCompanyMessages(company, [newMessage]);

    // Add to user's talent requests completed
    const user = loadUser();
    if (user && !user.talentRequestTypesCompleted.includes(talentRequest.type)) {
        user.talentRequestTypesCompleted.push(talentRequest.type);
        saveUser(user);
    }

    return true;
}

async function failTalentRequest(talentRequest, company, game) {  // fail_talent_request
    talentRequest.status = 'failed';
    const talent = await getTalentRequestTalent(talentRequest);
    const talentManager = getRandomStaffMemberByRole((await getCompanyStaff(company)), 'talent_manager');

    // Add company alert
    const talentRequestText = `${talent.name} (${await getTalentRequestName(talentRequest)})`;
    const newCompanyAlert = await createCompanyAlert('talentRequestFailed', talentRequestText);
    await addCompanyCompanyAlerts(company, [newCompanyAlert]);

    // Update morale
    const moraleChange = await getTalentRequestMoraleChange(talentRequest);
    for (const superstar of (await getRosterMemberSuperstars(talent))) {
        updateSuperstarMorale(superstar, moraleChange);
    }

    // Add message
    const newMessage = await createTalentRequestFailedMessage(talentRequest, talent, moraleChange, game.monthCount, game.year,
        talentManager);
    await addCompanyMessages(company, [newMessage]);

    return true;
}

async function getTalentRequestMoraleChange(talentRequest, declined = false) {  // get_talent_request_morale_change
    const talent = await getTalentRequestTalent(talentRequest);

    // Morale change depends on traits
    let moraleChange = 0;
    const superstars = await getRosterMemberSuperstars(talent);
    if (talentRequest.status === "failed") {
        if (superstars.some(x => x.traits.includes("Easy To Please") || x.traits.includes("Loyal"))) {
            moraleChange = getRandomInt(-14, -12);
        } else if (superstars.some(x => x.traits.includes("Hard To Please") || x.traits.includes("Rebel"))) {
            moraleChange = getRandomInt(-22, -20);
        } else {
            moraleChange = getRandomInt(-18, -16);
        }
    } else if (talentRequest.status === "completed") {
        if (superstars.some((x) => x.traits.includes("Easy To Please") || x.traits.includes("Loyal"))) {
            moraleChange = getRandomInt(16, 18);
        } else if (superstars.some((x) => x.traits.includes("Hard To Please") || x.traits.includes("Rebel"))) {
            moraleChange = getRandomInt(8, 10);
        } else {
            moraleChange = getRandomInt(12, 14);
        }
    }

    // Increase change for major requests
    if (talentRequestsData[talentRequest.type]['importance'] === "major") {
        moraleChange = Math.round(moraleChange * 1.4);
    }

    // Decrease change for declined requests
    if (declined) {
        moraleChange = Math.round(moraleChange * 0.85);
    }

    return moraleChange;
}

async function processTalentRequests(company, weeklyShow) {  // process_talent_requests
    let game = await getCompanyGame(company);

    // Process created/active requests
    let currentTalentRequests = (await getCompanyTalentRequests(company))
        .filter(x => ['created', 'active'].includes(x.status));
    for (let talentRequest of currentTalentRequests) {

        // Set active status
        if (talentRequest.status === 'created') {
            talentRequest.status = 'active';
        }

        // Decrease months remaining
        if (!weeklyShow) {
            if (talentRequest.monthsRemaining > 0) {
                talentRequest.monthsRemaining -= 1;
            }
        }

        // Check for end
        if (talentRequest.monthsRemaining <= 0) {

            // Complete request if negative request and secondary action completed (if present)
            if (talentRequestsData[talentRequest.type]['negative_request'] &&
                (!talentRequestsData[talentRequest.type]['secondary_action'] || talentRequest.actionCompleted)) {
                    await completeTalentRequest(talentRequest, company, game);
            } else {
                // Otherwise fail request
                await failTalentRequest(talentRequest, company, game);
            }
        }
    }

    return true;
}

async function checkTalentRequestCreation(company, rosterSuperstars, roster, championships) {  // check_talent_request_creation
    let requestsToAdd = 0;
    let companyTalentRequests = (await getCompanyTalentRequests(company))
        .filter(x => ['created', 'active'].includes(x.status));

    // Don't have more than maxRequests at a time
    let maxRequests = 5;
    if (companyTalentRequests.length < maxRequests) {
        let requestsToAddWeights = [[0, 30], [1, 50], [2, 15], [3, 5]];
        let maxRequestsToAdd = maxRequests - companyTalentRequests.length;
        requestsToAddWeights = requestsToAddWeights.filter(x => x[0] <= maxRequestsToAdd);
        requestsToAdd = weightedChoice(requestsToAddWeights);
    }

    // Add requests
    for (let x of Array(requestsToAdd).keys()) {
        let newTalentRequest = await addTalentRequest(company, companyTalentRequests, rosterSuperstars, roster, championships);
        if (newTalentRequest) {
            companyTalentRequests.push(newTalentRequest);
        }
    }

    return true;
}

async function addTalentRequest(company, companyTalentRequests, rosterSuperstars, roster, championships) {  // add_talent_request
    let talent = null;
    let requestMonths;
    let actionData;
    let requestType

    // Get request type, talent and months
    let count = 0;
    while (talent === null && count < 5) {  // Run again if no talent found
        requestType = selectTalentRequestType();
        let requestObjects = await getObjectsForTalentRequest(requestType, company, companyTalentRequests,
            rosterSuperstars, roster, championships);
        talent = requestObjects[0];
        requestMonths = requestObjects[1];
        actionData = requestObjects[2];
        count += 1;
    }

    if (!talent) {
        return null;
    }

    // Create talent request, add to company
    let newTalentRequest = await createTalentRequest(talent, requestType, requestMonths, actionData);
    await addCompanyTalentRequests(company, [newTalentRequest]);

    return newTalentRequest;
}

function selectTalentRequestType() {  // select_talent_request_type
    let requestTypesWeights = [];
    let rarityWeights = {'common': 3.8, 'normal': 3.5, 'uncommon': 2.15, 'rare': 1.25};
    for (let k in talentRequestsData) {
        let weight = rarityWeights[talentRequestsData[k]['rarity']];
        requestTypesWeights.push([k, weight]);
    }
    return weightedChoice(requestTypesWeights);
}

async function createTalentRequest() {  // create_talent_request
    return
}

function setupCreateTalentRequest(newFunction) {
    createTalentRequest = newFunction
}

async function getObjectsForTalentRequest(requestType, company, companyTalentRequests, rosterSuperstars, roster,
                                          championships) {  // get_objects_for_talent_request
    let actionData = {};
    let tagTeams = await getRosterTagTeams(roster);
    let talentRequestData = talentRequestsData[requestType];

    // Get available superstars
    let superstars = rosterSuperstars.filter(x => checkAvailability(x, true, false, false) &&
        x.activeEffect !== 'traded');

    // Check if request allows managers
    if (!talentRequestData['allow_managers']) {
        superstars = superstars.filter(x => !x.fullTimeManager);
    }

    // Remove superstars that already have a talent request of this type or have a conflicting request
    for (let talentRequest of companyTalentRequests) {
        if (requestType === talentRequest.type ||
            talentRequestsData[talentRequest.type]['conflicting_requests'].includes(requestType)) {
            let talentRequestTalent = await getTalentRequestTalent(talentRequest);
            let talentRequestTalentSuperstars = await getRosterMemberSuperstars(talentRequestTalent);
            superstars = superstars.filter(x => !objectInArray(x, talentRequestTalentSuperstars));
        }
    }

    // Get talent
    let talent = await getTalentForTalentRequest(requestType, company, superstars, tagTeams, roster, championships,
        rosterSuperstars);

    // Get action data if talent found
    if (talent && talentRequestData['get_action_data']) {
        actionData = await getActionDataForTalentRequest(requestType, talent, superstars, tagTeams, championships,
            roster, company);

        // If action data error, don't set talent
        if (!actionData || Object.keys(actionData).length === 0) {
            talent = null;
        }
    }

    let requestMonths = getRandomChoice(talentRequestData['months']);

    return [talent, requestMonths, actionData];
}

async function getTalentForTalentRequest(requestType, company, superstars, teams, roster, championships,
                                         rosterSuperstars) {  // get_talent_for_talent_request
    // Can return None if no talent found
    let talent = null;

    try {
        if (requestType === 'be_in_main_event') {
            let weights = [];
            for (let x of superstars) {
                let weight = 1;
                if ((await getRosterMemberSuperstars(x)).some(y => y.unhappy)) {
                    weight += 20;
                }
                if (['Spotlight Lover', 'Rebel', 'Hard To Please'].some(y => x.traits.includes(y))) {
                    weight += 40;
                }
                if (getRankFromPopularity(x.popularity) === 'Upper Midcarder') {
                    weight *= 3;
                }
                if (getRankFromPopularity(x.popularity) === 'Jabroni') {
                    weight *= 0.25;
                }
                weights.push([x, weight]);
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'go_to_next_rank') {
            let weights = [];
            for (let x of superstars) {
                let nextRank = await getNextRank(x);
                if (nextRank) {
                    let nextRankPopularity = {'Main Eventer': 800, 'Upper Midcarder': 600, 'Lower Midcarder': 400};
                    let popularityToNextRank = nextRankPopularity[nextRank] - x.popularity;
                    if (10 < popularityToNextRank && popularityToNextRank < 80) {
                        let weight = 1;
                        if ((await getRosterMemberSuperstars(x)).some(y => y.unhappy)) {
                            weight += 10;
                        }
                        if (['Spotlight Lover', 'Rebel', 'Hard To Please'].some(y => x.traits.includes(y))) {
                            weight += 10;
                        }
                        weights.push([x, weight]);
                    }
                }
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'no_clean_loss') {
            let weights = [];
            for (let x of superstars) {
                let weight = 0.1;
                if ((await getRosterMemberSuperstars(x)).some(y => y.unhappy)) {
                    weight += 25;
                }
                if (['Rebel', 'Hard To Please'].some(y => x.traits.includes(y))) {
                    weight += 50;
                }
                if (getRankFromPopularity(x.popularity) === 'Main Eventer') {
                    weight *= 2;
                    if (x.faceOfTheCompany) {
                        weight *= 2;
                    }
                } else {
                    weight *= 0.1;
                }
                weights.push([x, weight]);
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'put_x_over_clean') {
            let weights = [];
            for (let x of superstars) {
                let weight = 0.1;
                if (['Spotlight Lover', 'Giving Veteran', 'Star Maker'].some(y => x.traits.includes(y))) {
                    weight += 50;
                }
                weights.push([x, weight]);
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'feud_with_x') {
            let weights = [];
            for (let x of superstars) {
                let weight = 0.1;
                if (x.traits.includes('Storyteller')) {
                    weight += 20;
                }
                weights.push([x, weight]);
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'team_with_x') {
            let weights = [];
            for (let x of superstars) {
                let superstarTagTeam = (await getSuperstarTagTeams(x)).find(y => !y.temporary);
                if (!superstarTagTeam) {
                    let weight = 0.1;
                    if (x.traits.includes('Tag Team Specialist')) {
                        weight += 20;
                    }
                    weights.push([x, weight]);
                }
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'disband_team') {
            let weights = [];
            for (let x of superstars) {
                let superstarTagTeam = (await getSuperstarTagTeams(x)).find(y => !y.temporary);
                if (superstarTagTeam) {
                    let weight = 1;
                    if (x.hot) {
                        weight += 10;
                    }
                    weights.push([x, weight]);
                }
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'change_appearance') {
            let weights = [];
            for (let x of superstars) {
                let weight = 0.1;
                if (x.stale) {
                    weight += 5;
                }
                weights.push([x, weight]);
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'lose_next_match') {
            let weights = [];
            for (let x of superstars) {
                if (x.winsCounter >= 2) {
                    let weight = 0.1;
                    if (x.winsCounter >= 5) {
                        weight += 5;
                    }
                    if (['Rebel', 'Hard To Please'].some(y => x.traits.includes(y))) {
                        weight *= 0.1;
                    }
                    if (['Giving Veteran', 'Star Maker'].some(y => x.traits.includes(y))) {
                        weight += 10;
                    }
                    weights.push([x, weight]);
                }
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'beat_main_eventer') {
            let weights = [];
            for (let x of superstars) {
                let weight = 1;
                if ((await getRosterMemberSuperstars(x)).some(y => y.unhappy)) {
                    weight += 10;
                }
                if (['Spotlight Lover', 'Rebel', 'Hard To Please'].some(y => x.traits.includes(y))) {
                    weight += 10;
                }
                if (getRankFromPopularity(x.popularity) === 'Upper Midcarder') {
                    weight *= 4;
                }
                if (['Main Eventer', 'Jabroni'].includes(getRankFromPopularity(x.popularity))) {
                    weight *= 0.1;
                }
                weights.push([x, weight]);
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'beat_x_clean') {
            let weights = [];
            for (let x of superstars) {
                let weight = 0.1;
                if (['Rebel', 'Hard To Please'].some(y => x.traits.includes(y))) {
                    weight += 25;
                }
                if (getRankFromPopularity(x.popularity) === 'Main Eventer') {
                    weight *= 2;
                    if (x.faceOfTheCompany) {
                        weight *= 2;
                    }
                } else {
                    weight *= 0.1;
                }
                weights.push([x, weight]);
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'win_and_not_lose') {
            let weights = [];
            for (let x of superstars) {
                let weight = 0.1;
                if ((await getRosterMemberSuperstars(x)).some(y => y.unhappy)) {
                    weight += 20;
                }
                if (['Rebel', 'Hard To Please'].some(y => x.traits.includes(y))) {
                    weight += 20;
                }
                if (getRankFromPopularity(x.popularity) === 'Main Eventer') {
                    weight *= 2;
                    if (x.faceOfTheCompany) {
                        weight *= 2;
                    }
                } else {
                    weight *= 0.1;
                }
                weights.push([x, weight]);
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'win_next_match') {
            let weights = [];
            for (let x of superstars) {
                if (x.lossesCounter >= 2) {
                    let weight = 0.1;
                    if (x.lossesCounter >= 5) {
                        weight += 4;
                    }
                    if (['Easy To Please', 'Loyal'].some(y => x.traits.includes(y))) {
                        weight *= 0.5;
                    }
                    if (['Rebel', 'Hard To Please'].some(y => x.traits.includes(y))) {
                        weight += 8;
                    }
                    weights.push([x, weight]);
                }
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'be_managed') {
            let weights = [];
            for (let x of superstars) {
                if (!x.managerId && !x.fullTimeManager && x.charisma <= 2) {
                    let weight = 0.1;
                    if (x.charisma <= 1) {
                        weight *= 2;
                    }
                    weights.push([x, weight]);
                }
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'manage_x') {
            let weights = [];
            let managerIds = [...new Set(superstars.filter(x => x.managerId).map(x => x.managerId))];
            for (let x of superstars) {
                if (x.charisma >= 2 && !managerIds.includes(x.$loki)) {
                    let weight = 0.1;
                    if (x.fullTimeManager) {
                        weight += 15;
                    }
                    if (x.charisma >= 3) {
                        weight *= 3;
                    }
                    weights.push([x, weight]);
                }
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'start_faction') {
            let weights = [];
            let rosterFactions = await getRosterActiveFactions(roster);
            let rosterFactionSuperstars = (await Promise.all(rosterFactions.map(x => getFactionSuperstars(x)))).flat();
            for (let x of superstars) {
                if (!objectInArray(x, rosterFactionSuperstars)) {
                    let weight = 0.1;
                    if (getRankFromPopularity(x.popularity) === 'Upper Midcarder') {
                        weight *= 3;
                    }
                    if (getRankFromPopularity(x.popularity) === 'Jabroni') {
                        weight *= 0.2;
                    }
                    weights.push([x, weight]);
                }
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'leave_faction') {
            let weights = [];
            let rosterFactions = await getRosterActiveFactions(roster);
            let rosterFactionSuperstars = (await Promise.all(rosterFactions.map(x => getFactionSuperstars(x)))).flat();
            for (let x of superstars) {
                if (objectInArray(x, rosterFactionSuperstars)) {
                    let weight = 1;
                    if (x.hot) {
                        weight += 5;
                    }
                    weights.push([x, weight]);
                }
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'be_in_title_match') {
            let weights = [];
            for (let x of superstars) {
                if (!championships.some(c => c.superstarId === x.$loki)) {
                    let weight = 1;
                    if ((await getRosterMemberSuperstars(x)).some(y => y.unhappy)) {
                        weight += 10;
                    }
                    if (x.traits.includes('Spotlight Lover')) {
                        weight += 20;
                    }
                    if (getRankFromPopularity(x.popularity) === 'Upper Midcarder') {
                        weight *= 3;
                    }
                    if (getRankFromPopularity(x.popularity) === 'Jabroni') {
                        weight *= 0.1;
                    }
                    weights.push([x, weight]);
                }
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'match_with_x') {
            let weights = [];
            for (let x of superstars) {
                let weight = 0.1;
                if (x.workrate >= 3) {
                    weight += 10;
                }
                if (x.traits.includes('Ring General') || x.traits.includes('Workhorse')) {
                    weight += 20;
                }
                weights.push([x, weight]);
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'solo_promo_segment') {
            let weights = [];
            for (let x of superstars) {
                let weight = 1;
                if (x.traits.includes('Spotlight Lover')) {
                    weight *= 5;
                }
                if (['Main Eventer', 'Jabroni'].includes(getRankFromPopularity(x.popularity)) || x.hot) {
                    weight *= 0.5;
                }
                if (x.charisma === 2) {
                    weight *= 5;
                }
                if ((await getRosterMemberAge(x)) < 35) {
                    weight *= 3;
                }
                weights.push([x, weight]);
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'no_weekly_shows') {
            let game = await getCompanyGame(company);
            if (game.numberOfWeeklyShows !== 0) {
                let weights = [];
                for (let x of superstars) {
                    let weight = 0.1;
                    if (x.partTimer) {
                        weight += 20;
                    }
                    if (x.traits.includes('Other Interests') || x.traits.includes('Weekly Sloth')) {
                        weight += 40;
                    }
                    weights.push([x, weight]);
                }
                talent = weightedChoice(weights);
            }
        }

        if (requestType === 'customize_championship') {
            let game = await getCompanyGame(company);
            let weights = [];
            for (let x of superstars) {
                let superstarChampionships = championships.filter(c => c.superstarId === x.$loki && !c.matchContract &&
                    !c.championshipContract);
                for (let c of superstarChampionships) {
                    let reign = await getChampionshipCurrentReign(c);
                    if (reign.endMonth === null &&
                        getMonthsDifference(game.monthCount, game.year, reign.startMonth, reign.startYear) < 4) {
                        weights.push([x, 1]);
                        break;
                    }
                }
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'change_alignment') {
            let weights = [];
            for (let x of superstars) {
                if (x.alignment) {
                    let weight = 1 + x.alignmentMonths;
                    if (x.alignmentMonths >= 12) {
                        weight *= 10;
                    }
                    if (x.traits.includes('Natural Babyface') && x.alignment === 'heel') {
                        weight *= 40;
                    }
                    if (x.traits.includes('Natural Heel') && x.alignment === 'face') {
                        weight *= 40;
                    }
                    weights.push([x, weight]);
                }
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'debut_new_gimmick') {
            let weights = [];
            for (let x of superstars) {
                if (x.overness < 40) {
                    let weight = 1;
                    if (x.stale) {
                        weight += 15;
                    }
                    weights.push([x, weight]);
                }
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'vacate_championship') {
            let weights = [];
            for (let x of superstars) {
                if (championships.some(c => c.superstarId === x.$loki && !c.matchContract && !c.championshipContract &&
                    !c.tagTeam && !c.trio)) {
                    let weight = 1;
                    if ((await getRosterMemberSuperstars(x)).some(y => y.unhappy)) {
                        weight += 25;
                    }
                    if (x.traits.includes('Rebel') || x.traits.includes('Hard To Please')) {
                        weight += 75;
                    }
                    if (x.faceOfTheCompany) {
                        weight *= 5;
                    }
                    weights.push([x, weight]);
                }
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'be_in_gimmick_match_x') {
            let weights = [];
            for (let x of superstars) {
                let weight = 0.1;
                if (['Risk Taker', 'Gifted Acrobat', 'Enjoys Gimmick Matches'].some(y => x.traits.includes(y)) ||
                    x.traits.some(y => y.split(' ')[y.split(' ').length - 1] === 'Expert')) {
                    weight += 30;
                }
                weights.push([x, weight]);
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'join_faction') {
            let weights = [];
            let rosterFactions = await getRosterActiveFactions(roster);
            let rosterFactionSuperstars = (await Promise.all(rosterFactions.map(x => getFactionSuperstars(x)))).flat();
            for (let x of superstars) {
                if (!objectInArray(x, rosterFactionSuperstars)) {
                    let weight = 1;
                    if (getRankFromPopularity(x.popularity) === 'Upper Midcarder') {
                        weight *= 3;
                    }
                    if (getRankFromPopularity(x.popularity) === 'Jabroni') {
                        weight *= 0.2;
                    }
                    weights.push([x, weight]);
                }
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'change_brand') {
            let weights = [];
            for (let x of superstars) {
                if (x.brandName) {
                    let weight = 1;
                    if ((await getRosterMemberSuperstars(x)).some(y => y.unhappy)) {
                        weight += 5;
                    }
                    weights.push([x, weight]);
                }
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'win_a_championship') {
            let weights = [];
            for (let x of superstars) {
                if (!championships.some(c => c.superstarId === x.$loki)) {
                    let weight = 0.1;
                    if ((await getRosterMemberSuperstars(x)).some(y => y.unhappy)) {
                        weight += 50;
                    }
                    if (['Rebel', 'Hard To Please'].some(y => x.traits.includes(y))) {
                        weight += 25;
                    }
                    weights.push([x, weight]);
                }
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'lose_championship') {
            let weights = [];
            for (let x of superstars) {
                if (championships.some(c => c.superstarId === x.$loki && !c.matchContract && !c.championshipContract)) {
                    let weight = 1;
                    if ((await getRosterMemberSuperstars(x)).some(y => y.unhappy)) {
                        weight += 15;
                    }
                    if ((await getRosterMemberSuperstars(x)).some(y => y.contractMonthsRemaining)) {
                        weight += 30;
                    }
                    if (['Big Stage Wreck', 'Other Interests', 'Extreme Emotions'].some(y => x.traits.includes(y))) {
                        weight += 30;
                    }
                    weights.push([x, weight]);
                }
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'be_in_gimmick_match') {
            let weights = [];
            for (let x of superstars) {
                let weight = 0.1;
                if (['Risk Taker', 'Gifted Acrobat', 'Enjoys Gimmick Matches'].some(y => x.traits.includes(y))) {
                    weight += 30;
                }
                weights.push([x, weight]);
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'write_off') {
            let weights = [];
            for (let x of superstars) {
                let weight = 0.1;
                if (x.partTimer) {
                    weight += 15;
                }
                if (x.traits.includes('Other Interests')) {
                    weight += 50;
                }
                weights.push([x, weight]);
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'be_on_monthly_event') {
            // Get superstars used at latest monthly event
            let latestHeldEvent = (await getCompanyEvents(company)).filter(x => x.rating !== null).sort((a, b) => b.$loki - a.$loki)[0];
            let matchesAndSegments = await getEventMatchesAndSegments(latestHeldEvent);
            let superstarsUsedAtEvent = await getSuperstarsUsedAtEvent(matchesAndSegments, rosterSuperstars);

            // Get bottom 25% superstars by popularity
            let sortedSuperstars = superstars.sort((a, b) => a.popularity - b.popularity);
            let bottomPopularitySuperstars = sortedSuperstars.slice(0, Math.floor(sortedSuperstars.length * 0.25));

            let weights = [];
            for (let x of superstars) {
                // Check if not featured on last monthly event
                if (!objectInArray(x, superstarsUsedAtEvent) && checkSuperstarBrandAvailability(latestHeldEvent, x)) {
                    let weight = 1;
                    // Check if part of low popularity superstars and not Jabroni
                    if (objectInArray(x, bottomPopularitySuperstars) && getRankFromPopularity(x.popularity) !== 'Jabroni') {
                        weight += 15;
                    }
                    weights.push([x, weight]);
                }
            }
            talent = weightedChoice(weights);
        }

        if (requestType === 'train_skill_x') {
            if (getAvailableStaffMembersByRole((await getCompanyStaff(company)), 'trainer')
                .some(x => !x.currentTask)) {
                let weights = [];
                for (let x of superstars) {
                    if (x.workrate < 3 || x.charisma < 3) {
                        let weight = 0.1;
                        if (['Loves Training', 'Quick Learner'].some(y => x.traits.includes(y))) {
                            weight += 25;
                        }
                        if (['Hates Training', 'Slow Learner'].some(y => x.traits.includes(y))) {
                            weight *= 0.1;
                        }
                        weights.push([x, weight]);
                    }
                }
                talent = weightedChoice(weights);
            }
        }
    } catch (error) {}

    return talent;
}

async function getActionDataForTalentRequest(requestType, talent, superstars, teams, championships, roster, company) {  // get_action_data_for_talent_request
    let actionData = {};

    try {
        if (requestType === 'go_to_next_rank') {
            actionData['selectedRank'] = await getNextRank(talent);
        }

        if (requestType === 'put_x_over_clean') {
            let opponentWeights = [];
            for (let x of superstars) {
                if (x.$loki !== talent.$loki && x.gender === talent.gender && x.brandName === talent.brandName) {
                    let weight = 1;
                    let popularityDifference = (await getRosterMemberPopularity(talent)) - (await getRosterMemberPopularity(x));
                    if (50 < popularityDifference && popularityDifference < 150) {
                        weight += 20;
                    }
                    let superstarAge = await getRosterMemberAge(x);
                    let ageDifference = (await getRosterMemberAge(talent)) - superstarAge;
                    if (ageDifference > 8 || superstarAge < 30) {
                        weight += 10;
                    }
                    if (superstarAge > 40) {
                        weight /= 5;
                    }
                    opponentWeights.push([x, weight]);
                }
            }
            let selectedOpponent = weightedChoice(opponentWeights);
            actionData['selectedTalentId'] = selectedOpponent.$loki;
        }

        if (requestType === 'feud_with_x') {
            let opponentWeights = [];
            let currentFeuds = (await getRosterMemberFeuds(talent)).filter(x => x.started && !x.ended);
            let feudingSuperstars = (await Promise.all(currentFeuds.map(x => getFeudParticipants(x)))).flat();
            for (let x of superstars) {
                if (x.$loki !== talent.$loki && x.gender === talent.gender && x.alignment !== talent.alignment &&
                    x.brandName === talent.brandName && !objectInArray(x, feudingSuperstars)) {
                    let weight = 0.1;
                    let popularityDifference = Math.abs(
                        (await getRosterMemberPopularity(talent)) - (await getRosterMemberPopularity(x)));
                    if (popularityDifference < 200) {
                        weight += 10;
                    }
                    if (x.hot) {
                        weight += 100;
                    }
                    opponentWeights.push([x, weight]);
                }
            }
            let selectedOpponent = weightedChoice(opponentWeights);
            actionData['selectedTalentId'] = selectedOpponent.$loki;
        }

        if (requestType === 'team_with_x') {
            let opponentWeights = [];
            for (let x of superstars) {
                if (x.$loki !== talent.$loki && x.gender === talent.gender && x.alignment === talent.alignment &&
                    x.brandName === talent.brandName) {
                    let superstarTagTeam = (await getSuperstarTagTeams(x)).find(y => !y.temporary);
                    if (!superstarTagTeam) {
                        let weight = 0.1;
                        let popularityDifference = Math.abs(
                            (await getRosterMemberPopularity(talent)) - (await getRosterMemberPopularity(x)));
                        if (popularityDifference < 100) {
                            weight += 10;
                        }
                        opponentWeights.push([x, weight]);
                    }
                }
            }
            let selectedOpponent = weightedChoice(opponentWeights);
            actionData['selectedTalentId'] = selectedOpponent.$loki;
        }

        if (requestType === 'disband_team') {
            let talentTeam = (await getSuperstarTagTeams(talent)).find(x => !x.temporary);
            actionData['selectedTeamId'] = talentTeam.$loki;
        }

        if (requestType === 'beat_x_clean') {
            let opponentWeights = [];
            for (let x of superstars) {
                if (x.$loki !== talent.$loki && x.gender === talent.gender && x.brandName === talent.brandName) {
                    let weight = 0.1;
                    if (x.overness < 60) {
                        weight *= 0.1;
                    }
                    if (x.hot) {
                        weight += 50;
                    }
                    opponentWeights.push([x, weight]);
                }
            }
            let selectedOpponent = weightedChoice(opponentWeights);
            actionData['selectedTalentId'] = selectedOpponent.$loki;
        }

        if (requestType === 'manage_x') {
            let superstarToManageWeights = [];
            for (let x of superstars) {
                if (x.$loki !== talent.$loki && !x.managerId && !x.fullTimeManager && x.charisma <= 2) {
                    let weight = 0.1;
                    if (x.charisma <= 1) {
                        weight *= 2;
                    }
                    superstarToManageWeights.push([x, weight]);
                }
            }
            let selectedTalent = weightedChoice(superstarToManageWeights);
            actionData['selectedTalentId'] = selectedTalent.$loki;
        }

        if (requestType === 'leave_faction') {
            let talentFaction = (await getSuperstarActiveFactions(talent))[0];
            actionData['selectedFactionId'] = talentFaction.$loki;
        }

        if (requestType === 'match_with_x') {
            let opponentWeights = [];
            for (let x of superstars) {
                if (x.$loki !== talent.$loki && x.gender === talent.gender && x.brandName === talent.brandName) {
                    let weight = 0.1;
                    if (x.workrate >= 3) {
                        weight += 10;
                    }
                    if (['Ring General', 'Workhorse'].some(y => x.traits.includes(y))) {
                        weight += 20;
                    }
                    opponentWeights.push([x, weight]);
                }
            }
            let selectedOpponent = weightedChoice(opponentWeights);
            actionData['selectedTalentId'] = selectedOpponent.$loki;
        }

        if (['lose_championship', 'customize_championship'].includes(requestType)) {
            let talentChampionships = championships.filter(x => x.superstarId === talent.$loki && !x.matchContract &&
                !x.championshipContract);
            let talentChampionship = getArrayMaxByProperty(talentChampionships, 'xp');
            actionData['selectedChampionshipId'] = talentChampionship.$loki;
        }

        if (requestType === 'vacate_championship') {
            let talentChampionships = championships.filter(x => x.superstarId === talent.$loki && !x.matchContract
                && !x.championshipContract && !x.tagTeam && !x.trio);
            let talentChampionship = getArrayMaxByProperty(talentChampionships, 'xp');
            actionData['selectedChampionshipId'] = talentChampionship.$loki;
        }

        if (requestType === 'change_alignment') {
            let selectedAlignment;
            if (talent.alignment === 'face') {
                selectedAlignment = 'heel';
            } else if (talent.alignment === 'heel') {
                selectedAlignment = 'face';
            }
            actionData['selectedAlignment'] = selectedAlignment;
        }

        if (requestType === 'join_faction') {
            let rosterFactions = await getRosterActiveFactions(roster);
            let factionWeights = [];
            for (let x of rosterFactions) {
                let weight = 1;
                weight += x.reputation;
                factionWeights.push([x, weight]);
            }
            let selectedFaction = weightedChoice(factionWeights);
            actionData['selectedFactionId'] = selectedFaction.$loki;
        }

        if (requestType === 'change_brand') {
            // Get exclusive brands that are not talent's brand
            let exclusiveRosterBrands = (await getRosterBrands(roster))
                .filter(x => x.weeklyShowIsExclusive && x.name !== talent.brandName);
            let brandWeights = [];
            for (let x of exclusiveRosterBrands) {
                let weight = 1;
                if (x.hot) {
                    weight += 5;
                }
                brandWeights.push([x, weight]);
            }
            let selectedBrand = weightedChoice(brandWeights);
            actionData['selectedBrandId'] = selectedBrand.$loki;
        }

        if (requestType === 'train_skill_x') {
            let availableSkills = [];
            if (talent.workrate < 3) {
                let weight = 3 - talent.workrate;
                availableSkills.push(['workrate', weight]);
            }
            if (talent.charisma < 3) {
                let weight = 3 - talent.charisma;
                availableSkills.push(['charisma', weight]);
            }
            actionData['selectedSkill'] = weightedChoice(availableSkills);
        }

        if (requestType === 'be_in_gimmick_match_x') {
            // Remove contract, unavailable, battle royal and some consequence matches
            let gimmickMatches = (await getCompanyGimmickMatches(company)).filter(x => x.available && x.rules !== 'battle_royal' &&
                !x.cashinContractName && !x.matchContractName &&
                !['remove_hair', 'add_hair', 'remove_mask', 'remove_mask_or_hair'].includes(x.consequence));

            // Remove Femme Fatale if talent doesn't have female gender
            if (talent.gender !== 'female') {
                gimmickMatches = gimmickMatches.filter(x => x.name !== 'Femme Fatale');
            }

            // Select random gimmick match
            let gimmickMatch = getRandomChoice(gimmickMatches);

            // Lower chance to select pole or brawl matches
            if (gimmickMatch.generateObject || gimmickMatch.generateLocation) {
                gimmickMatch = getRandomChoice(gimmickMatches);
            }

            // High chance to select expert match
            for (let trait of talent.traits) {
                if (trait.includes('Match Expert')) {
                    let expertGimmickMatchName = trait.split(' Match Expert')[0];
                    let expertGimmickMatch = gimmickMatches.find(x => x.name === expertGimmickMatchName);
                    if (expertGimmickMatch && getRandomInt(0, 100) <= 75) {
                        gimmickMatch = expertGimmickMatch;
                    }
                }
            }

            actionData['selectedGimmickMatchId'] = gimmickMatch.$loki;
        }
    } catch (error) {}

    return actionData;
}

export {getCompanyTalentRequestsData, getTalentRequestData, checkTalentRequestAction, getTalentRequestName,
    processTalentRequests, checkTalentRequestCreation, acceptTalentRequestView, declineTalentRequestView,
    changeTalentRequestView, setupCreateTalentRequest}